import { useParams, useNavigate } from 'react-router-dom';
import { useGetConsentBySlugV1ConsentsSlugGet } from '../../../api/generated-catch-backend/consents/consents';
import { PortableText } from '../questionnaireV2/components/portable-text';
import DashboardNavigationBar from '../../../components/dashboard/navigation-bar/dashboard-navigation-bar';
import '../dashboard/dashboard-reset.css';

export function Page() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { data: consent, isLoading, error } = useGetConsentBySlugV1ConsentsSlugGet(
    slug || '',
    {
      query: {
        enabled: !!slug,
      },
    }
  );

  const handleBack = () => {
    navigate(-1);
  };

  // Format the updated date
  const formattedDate = consent?.updatedAtUtc
    ? new Date(consent.updatedAtUtc).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  // Map slug to display title
  const getTitle = (slug: string | undefined) => {
    if (!slug) return 'Consent Document';
    if (slug.includes('hipaa')) return 'HIPAA AUTHORIZATION';
    if (slug.includes('terms')) return 'TERMS OF SERVICE';
    return 'Consent Document';
  };

  return (
    <>
      <DashboardNavigationBar />
      <div className="dashboard-reset w-full">
        <div className="container mx-auto px-4 py-8">
          {/* Back Button */}
          <div className="mx-auto max-w-4xl mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm font-medium text-gray-900 transition-colors hover:text-gray-600"
              aria-label="Go back"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              BACK
            </button>
          </div>

          <div className="mx-auto max-w-4xl">
            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading consent document...</p>
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && (
              <div className="text-center py-16">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  Unable to Load Consent Document
                </h1>
                <p className="text-gray-600 mb-6">
                  We couldn't load the consent document. Please try again later.
                </p>
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors"
                >
                  Go Back
                </button>
              </div>
            )}

            {/* Success State */}
            {consent && !isLoading && !error && (
              <>
                {/* Title */}
                <h1 className="text-4xl font-bold text-center text-gray-900 mb-2">
                  {getTitle(slug)}
                </h1>

                {/* Last Updated */}
                {formattedDate && (
                  <p className="text-center text-sm text-gray-600 mb-8">
                    Last Updated: {formattedDate}
                  </p>
                )}

                {/* Content */}
                <div className="prose prose-gray max-w-none">
                  <PortableText
                    content={consent.content}
                    className="text-[14px] leading-[170%] text-gray-700"
                  />
                </div>

                {/* Bottom Back Button */}
                <div className="mt-12 text-center">
                  <button
                    onClick={handleBack}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Go Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
